import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button, Field, Icon, Input, MenuButton, Modal, Notice, Panel, Select } from '../../design/index.js'
import { DOCUMENTS, getDocumentDef } from '@project/contracts'
import { useApi } from '../../app/api.js'
import {
  adminApi, type CompanyAddress, type CompanyRecord, type CompanySetup, type CompanyTaxUnit, type NumberRule,
} from '../../app/admin-api.js'
import { ErrorBlock, Loading, messageFor } from '../../framework/feedback.js'
import { AdminList, textMatch, type AdminColumn } from './AdminList.js'
import { TeamBoard } from '../../framework/TeamBoard.js'
import { ChipChoice } from './pickers.js'
import './admin.css'

const YES_NO = [{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]

/* ============================== companies (SET-020, 021, 022) ============== */

const blankCompany: CompanyRecord = {
  id: '', name: '', legalName: '', abbreviation: '', gstin: null, panNo: null, email: null, active: true,
  tanNo: null, cinNo: null, landline: null, website: null, isTraining: false, hasLogo: false,
}

export const CompaniesPage = ({ companyId }: { companyId: string }) => {
  const api = useApi()
  const admin = adminApi(api)
  const [open, setOpen] = useState<CompanyRecord | null>(null)
  const companies = useQuery({ queryKey: ['adminCompanyRecords', api.companyId], queryFn: () => admin.companies() })

  if (open) return <CompanyRecordPage initial={open} onBack={() => setOpen(null)} />
  if (companies.isLoading) return <Loading />
  if (companies.error) return <ErrorBlock error={companies.error} />

  const columns: ReadonlyArray<AdminColumn<CompanyRecord>> = [
    { key: 'name', label: 'Name', locked: true, render: (r) => <>{r.name}{r.id === companyId ? <span className="cell-sub"> — you are in this one</span> : null}</> },
    { key: 'legalName', label: 'Legal Name' },
    { key: 'abbreviation', label: 'Abbreviation', width: 140, render: (r) => <span className="num">{r.abbreviation}</span> },
    { key: 'isTraining', label: 'Training Company?', width: 170, render: (r) => (r.isTraining ? 'Yes' : 'No') },
    { key: 'active', label: 'Active', width: 90, render: (r) => (r.active ? 'Yes' : 'No') },
    { key: 'gstin', label: 'GSTIN', width: 190, optional: true, render: (r) => <span className="num">{r.gstin ?? '—'}</span> },
    { key: 'panNo', label: 'PAN No', width: 140, optional: true, render: (r) => r.panNo ?? '—' },
  ]
  return (
    <AdminList
      title="All Companies"
      scope="admin:companies"
      rows={companies.data ?? []}
      columns={columns}
      getKey={(r) => r.id}
      onRowClick={(r) => setOpen(r)}
      actions={<Button kind="primary" icon={<Icon name="plus" size={18} />} onClick={() => setOpen(blankCompany)}>Create Company</Button>}
      filters={[
        { key: 'name', label: 'Name', matches: (r, v) => textMatch(`${r.name} ${r.legalName}`, v) },
        { key: 'training', label: 'Training Company?', options: YES_NO, matches: (r, v) => r.isTraining === (v === 'yes') },
        { key: 'active', label: 'Active', options: YES_NO, matches: (r, v) => r.active === (v === 'yes') },
      ]}
      exportColumns={[
        { header: 'Name', value: (r) => r.name },
        { header: 'Legal Name', value: (r) => r.legalName },
        { header: 'Abbreviation', value: (r) => r.abbreviation },
        { header: 'Training Company?', value: (r) => (r.isTraining ? 'Yes' : 'No') },
        { header: 'Active', value: (r) => (r.active ? 'Yes' : 'No') },
      ]}
    />
  )
}

const readPng = (file: File): Promise<string> => new Promise((resolve, reject) => {
  const reader = new FileReader()
  reader.onload = () => resolve(String(reader.result))
  reader.onerror = () => reject(new Error('That file could not be read.'))
  reader.readAsDataURL(file)
})

export const CompanyRecordPage = ({ initial, onBack }: { initial: CompanyRecord; onBack: () => void }) => {
  const api = useApi()
  const admin = adminApi(api)
  const client = useQueryClient()
  const [c, setC] = useState<CompanyRecord & { logo?: string | null }>(initial)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  const isNew = c.id === ''

  const save = useMutation({
    mutationFn: (over: Partial<CompanyRecord> = {}) => admin.saveCompany({ ...c, ...over, ...(c.id ? { id: c.id } : { id: undefined }) }),
    onSuccess: (next) => {
      client.setQueryData(['adminCompanyRecords', api.companyId], next)
      void client.invalidateQueries({ queryKey: ['companies'] })
      const same = next.find((x) => (c.id ? x.id === c.id : x.abbreviation === c.abbreviation.trim().toUpperCase()))
      if (same) setC(same)
      setError(null)
      setSaved(true)
    },
    onError: (err) => setError(messageFor(err)),
  })
  const set = <K extends keyof CompanyRecord>(k: K, v: CompanyRecord[K]): void => { setC({ ...c, [k]: v }); setSaved(false) }

  return (
    <>
      <button type="button" className="crumb link-button" onClick={onBack}><Icon name="left" size={15} /> All Companies</button>
      {/*
        SET1-038 — the record's header: the name with its Active chip, then
        Close and Action ▾ (Save, Deactivate / Activate) on the right.
      */}
      <div className="doc-head" style={{ alignItems: 'center' }}>
        <h2 className="page-title" style={{ margin: 0 }}>
          {isNew ? 'Create Company' : c.name} {!isNew ? <span className="chip">{c.active ? 'Active' : 'Inactive'}</span> : null}
        </h2>
        <div className="doc-actions">
          <Button kind="ghost" onClick={onBack}>Close</Button>
          <MenuButton label="Action" kind="primary" icon="down" showChevron
            disabled={save.isPending}
            items={[
              { key: 'save', label: isNew ? 'Create' : 'Save', onSelect: () => {
                if (c.name.trim().length < 2 || c.abbreviation.trim().length < 2) { setError('Give the company a name and an abbreviation of at least two characters.'); return }
                save.mutate({})
              } },
              ...(isNew ? [] : [{
                key: 'active', label: c.active ? 'Deactivate' : 'Activate', danger: c.active,
                onSelect: () => { setC({ ...c, active: !c.active }); save.mutate({ active: !c.active }) },
              }]),
            ]} />
        </div>
      </div>
      {error ? <div style={{ margin: '12px 0' }}><Notice kind="bad">{error}</Notice></div> : null}
      {saved ? <div style={{ margin: '12px 0' }}><Notice kind="ok">Saved.</Notice></div> : null}

      <Panel title="General Info">
        <div className="form-grid">
          <Field label="Name" required><Input value={c.name} onChange={(v) => set('name', v)} /></Field>
          <Field label="Legal Name" required hint="As registered. Printed on invoices."><Input value={c.legalName} onChange={(v) => set('legalName', v)} /></Field>
          <Field label="Abbreviation" required hint="Two to ten letters or digits, used in document numbers.">
            <Input value={c.abbreviation} onChange={(v) => set('abbreviation', v)} />
          </Field>
          <Field label="Training Company?" hint="A sandbox for training: nothing it does is emailed out or sent to accounting.">
            <button type="button" role="switch" aria-checked={c.isTraining} aria-label="Training Company?"
              className={c.isTraining ? 'switch switch--on' : 'switch'} onClick={() => set('isTraining', !c.isTraining)}>
              <span className="switch__knob" />
              <span className="switch__text">{c.isTraining ? 'Yes' : 'No'}</span>
            </button>
          </Field>
          <Field label="Upload a Logo" hint={c.hasLogo ? 'A logo is set. Choose a file to replace it. PNG, under 100 kB.' : 'PNG, under 100 kB.'}>
            <input type="file" accept="image/png" aria-label="Upload a Logo"
              onChange={(e) => {
                const file = e.currentTarget.files?.[0]
                if (file) void readPng(file).then((logo) => setC({ ...c, logo }))
              }} />
          </Field>
        </div>
      </Panel>
      <Panel title="Contact Info">
        <div className="form-grid">
          <Field label="Landline"><Input value={c.landline ?? ''} onChange={(v) => set('landline', v)} /></Field>
          <Field label="Email"><Input value={c.email ?? ''} onChange={(v) => set('email', v)} /></Field>
          <Field label="Website"><Input value={c.website ?? ''} onChange={(v) => set('website', v)} /></Field>
        </div>
      </Panel>
      <Panel title="Registration Info">
        <div className="form-grid">
          <Field label="PAN No"><Input value={c.panNo ?? ''} onChange={(v) => set('panNo', v)} /></Field>
          <Field label="TAN No"><Input value={c.tanNo ?? ''} onChange={(v) => set('tanNo', v)} /></Field>
          <Field label="CIN No"><Input value={c.cinNo ?? ''} onChange={(v) => set('cinNo', v)} /></Field>
          <Field label="GSTIN" hint="The company's principal GSTIN. Each further one is a tax unit below."><Input value={c.gstin ?? ''} onChange={(v) => set('gstin', v)} /></Field>
        </div>
      </Panel>

      {isNew ? (
        <>
          <Panel title="Address Info"><p className="muted">You can add addresses after creating the company.</p></Panel>
          <Panel title="Tax Unit Info"><p className="muted">You can add tax units after creating the company.</p></Panel>
        </>
      ) : (
        <>
          <CompanyAddresses companyId={c.id} companyName={c.name} />
          <TeamBoard record={{ type: 'company', id: c.id }} />
        </>
      )}
    </>
  )
}

const blankAddress = { label: '', line1: '', line2: '', city: '', state: '', stateCode: '', postalCode: '', country: 'India', gstin: '' }

const CompanyAddresses = ({ companyId, companyName }: { companyId: string; companyName: string }) => {
  const api = useApi()
  const admin = adminApi(api)
  const client = useQueryClient()
  const key = ['companySetup', api.companyId, companyId]
  const setup = useQuery({ queryKey: key, queryFn: () => admin.companySetup(companyId) })
  const [address, setAddress] = useState<(typeof blankAddress & { id?: string }) | null>(null)
  const [unit, setUnit] = useState<{ id?: string; name: string; gstin: string; addressId: string } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const run = useMutation({
    mutationFn: (fn: () => Promise<CompanySetup>) => fn(),
    onSuccess: (next) => { client.setQueryData(key, next); setAddress(null); setUnit(null); setError(null) },
    onError: (err) => setError(messageFor(err)),
  })
  if (setup.isLoading) return <Loading />
  if (setup.error) return <ErrorBlock error={setup.error} />
  const { addresses, taxUnits } = setup.data!
  const def = addresses.find((a) => a.isDefault)
  const line = (a: CompanyAddress): string => [a.line1, a.line2, a.city, a.state, a.postalCode, a.country].filter(Boolean).join(', ')

  return (
    <>
      {error ? <div style={{ margin: '12px 0' }}><Notice kind="bad">{error}</Notice></div> : null}
      <Panel title="Address Info" aside={<Button kind="ghost" icon={<Icon name="plus" size={16} />} onClick={() => setAddress({ ...blankAddress })}>Address</Button>}>
        <div className="card-list">
          {addresses.map((a) => (
            <div className="card-item" key={a.id}>
              <div>
                <div className="card-item__title">{a.label}{a.isDefault ? <span className="chip" style={{ marginLeft: 8 }}>Default</span> : null}</div>
                <div className="cell-sub">{line(a) || '—'}</div>
                {a.gstin ? <div className="cell-sub num">GSTIN {a.gstin}</div> : null}
              </div>
              <span className="row">
                <Button kind="ghost" onClick={() => setAddress({
                  id: a.id, label: a.label ?? '', line1: a.line1 ?? '', line2: a.line2 ?? '', city: a.city ?? '', state: a.state ?? '',
                  stateCode: a.stateCode ?? '', postalCode: a.postalCode ?? '', country: a.country || 'India', gstin: a.gstin ?? '',
                })}>Edit</Button>
                <Button kind="ghost" aria-label={`Delete ${a.label ?? 'address'}`} onClick={() => run.mutate(() => admin.removeAddress(companyId, a.id))}>×</Button>
              </span>
            </div>
          ))}
          {addresses.length === 0 ? <p className="muted">No addresses yet.</p> : null}
        </div>
        {/* Under the cards, as the reference has it. */}
        <Field label="Default Address">
          <Select value={def?.id ?? ''} placeholder="Choose the default" onChange={(id) => run.mutate(() => admin.defaultAddress(companyId, id))}
            options={addresses.map((a) => ({ value: a.id, label: a.label ?? line(a) }))} />
        </Field>
      </Panel>

      <Panel title="Tax Unit Info" aside={<Button kind="ghost" icon={<Icon name="plus" size={16} />} onClick={() => setUnit({ name: `Default - ${companyName}`, gstin: '', addressId: def?.id ?? '' })}>Tax Unit</Button>}>
        <div className="card-list">
          {taxUnits.map((u: CompanyTaxUnit) => (
            <div className="card-item" key={u.id}>
              <div>
                <div className="card-item__title">{u.name} <span className="chip">{u.active ? 'Active' : 'Inactive'}</span></div>
                <div className="cell-sub num">GSTIN {u.gstin} · state {u.stateCode}</div>
                <div className="cell-sub">{u.addressLabel ?? 'No address'}</div>
              </div>
              <span className="row">
                <Button kind="ghost" onClick={() => setUnit({ id: u.id, name: u.name, gstin: u.gstin, addressId: u.addressId ?? '' })}>Edit</Button>
                {/* SET1-038 — refused by the server once a document carries it. */}
                <Button kind="ghost" aria-label={`Delete ${u.name}`} onClick={() => run.mutate(() => admin.removeTaxUnit(companyId, u.id))}>×</Button>
              </span>
            </div>
          ))}
          {taxUnits.length === 0 ? <p className="muted">No tax units yet.</p> : null}
        </div>
      </Panel>

      {address ? (
        <Modal title={address.id ? 'Edit address' : 'Add an address'} onClose={() => setAddress(null)}
          actions={<><Button kind="ghost" onClick={() => setAddress(null)}>Cancel</Button>
            <Button kind="primary" disabled={run.isPending || address.label.trim() === ''} onClick={() => run.mutate(() => admin.saveAddress(companyId, address))}>Save</Button></>}>
          <Field label="Name" required hint="e.g. Registered Office"><Input value={address.label} onChange={(label) => setAddress({ ...address, label })} /></Field>
          <Field label="Line 1"><Input value={address.line1} onChange={(line1) => setAddress({ ...address, line1 })} /></Field>
          <Field label="Line 2"><Input value={address.line2} onChange={(line2) => setAddress({ ...address, line2 })} /></Field>
          <div className="form-grid">
            <Field label="City"><Input value={address.city} onChange={(city) => setAddress({ ...address, city })} /></Field>
            <Field label="State"><Input value={address.state} onChange={(state) => setAddress({ ...address, state })} /></Field>
            <Field label="State code"><Input value={address.stateCode} onChange={(stateCode) => setAddress({ ...address, stateCode })} /></Field>
            <Field label="PIN code"><Input value={address.postalCode} onChange={(postalCode) => setAddress({ ...address, postalCode })} /></Field>
            <Field label="Country"><Input value={address.country} onChange={(country) => setAddress({ ...address, country })} /></Field>
          </div>
          <Field label="GSTIN"><Input value={address.gstin} onChange={(gstin) => setAddress({ ...address, gstin })} /></Field>
        </Modal>
      ) : null}
      {unit ? (
        <Modal title={unit.id ? 'Edit tax unit' : 'Add a tax unit'} onClose={() => setUnit(null)}
          actions={<><Button kind="ghost" onClick={() => setUnit(null)}>Cancel</Button>
            <Button kind="primary" disabled={run.isPending || unit.gstin.trim().length !== 15}
              onClick={() => run.mutate(() => admin.saveTaxUnit(companyId, { ...unit, addressId: unit.addressId || null }))}>Save</Button></>}>
          <Field label="Name"><Input value={unit.name} onChange={(name) => setUnit({ ...unit, name })} /></Field>
          <Field label="GSTIN" required hint="The state is read from its first two digits."><Input value={unit.gstin} onChange={(gstin) => setUnit({ ...unit, gstin })} /></Field>
          <Field label="Address">
            <Select value={unit.addressId} placeholder="No address" onChange={(addressId) => setUnit({ ...unit, addressId })}
              options={addresses.map((a) => ({ value: a.id, label: a.label ?? line(a) }))} />
          </Field>
        </Modal>
      ) : null}
    </>
  )
}

/* ============================== numbering (SET-023, 024, 025) ================ */

const SEPARATELY = [
  { value: 'COMPANY', label: 'Company' },
  { value: 'JOB_TYPE', label: 'Job type' },
  { value: 'TAX_UNIT', label: 'Tax unit (GSTIN)' },
]
/** What may be picked under Separate Numbering for; every counter is already per company. */
const SEPARATE_PICKS = SEPARATELY.filter((s) => s.value !== 'COMPANY')
const RESETS: Readonly<Record<string, string>> = { NEVER: 'Never', YEARLY: 'Each financial year', MONTHLY: 'Each month' }
const scopeWords = (scope: string): string =>
  scope.split(',').map((p) => SEPARATELY.find((s) => s.value === p)?.label ?? p).join(', ')

/** A document type's name as the module menu has it: `Credit Notes`. */
const docName = (docType: string, fallback: string): string => getDocumentDef(docType)?.labelPlural ?? fallback

/** The format split into its tags and the text between them. */
const segmentsOf = (format: string): Array<{ token: boolean; text: string }> =>
  format.split(/(\{[^}]*\})/).filter((x) => x !== '').map((text) => ({ token: /^\{[^}]*\}$/.test(text), text }))

/**
 * SET1-042 — the Number Format as the reference edits it: each tag a chip,
 * the text between them typed in place. Backspace in the empty box takes the
 * last piece off.
 */
const FormatInput = ({ value, onChange }: { value: string; onChange: (v: string) => void }) => {
  const [typed, setTyped] = useState('')
  const parts = segmentsOf(value)
  const commit = (): void => { if (typed !== '') { onChange(`${value}${typed}`); setTyped('') } }
  return (
    <div className="token-input" role="group" aria-label="Format">
      {parts.map((p, i) => (p.token ? (
        <span key={`${i}-${p.text}`} className="token-chip">
          {p.text.slice(1, -1)}
          <button type="button" aria-label={`Remove ${p.text.slice(1, -1)}`}
            onClick={() => onChange(parts.filter((_, j) => j !== i).map((x) => x.text).join(''))}>×</button>
        </span>
      ) : <span key={`${i}-${p.text}`} className="token-text">{p.text}</span>))}
      <input aria-label="Format text" value={typed} placeholder={parts.length === 0 ? 'Type text or click a tag below' : ''}
        onChange={(e) => setTyped(e.currentTarget.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); commit() }
          if (e.key === 'Backspace' && typed === '' && parts.length > 0) onChange(parts.slice(0, -1).map((x) => x.text).join(''))
        }} />
    </div>
  )
}

/** `FY2026-27` — the label a series row carries for the year it counts in. */
const currentFy = (today: Date = new Date()): string => {
  const y = today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1
  return `FY${y}-${String((y + 1) % 100).padStart(2, '0')}`
}

const blankRule = (docType = '', label = ''): NumberRule => ({
  id: '', docType, docTypeLabel: label, prefix: '', nextValue: 1, width: 4, financialYear: currentFy(), active: true,
  format: '{running_number}', scope: 'COMPANY', resetPeriod: 'YEARLY', jobTypePlaceholder: '', example: '',
})

export const NumberingPage = () => {
  const api = useApi()
  const admin = adminApi(api)
  const client = useQueryClient()
  const [editing, setEditing] = useState<NumberRule | null>(null)
  const rules = useQuery({ queryKey: ['adminNumberRules', api.companyId], queryFn: () => admin.numbering() })

  if (editing) {
    return (
      <NumberRulePage
        initial={editing}
        taken={(rules.data ?? []).map((r) => r.docType)}
        onBack={() => setEditing(null)}
        onSaved={(next) => client.setQueryData(['adminNumberRules', api.companyId], next)}
      />
    )
  }
  if (rules.isLoading) return <Loading />
  if (rules.error) return <ErrorBlock error={rules.error} />

  const columns: ReadonlyArray<AdminColumn<NumberRule>> = [
    { key: 'docTypeLabel', label: 'Document', locked: true },
    { key: 'active', label: 'Active', width: 90, render: (r) => (r.active ? 'Yes' : 'No') },
    { key: 'example', label: 'The next one will be', width: 220, optional: true, render: (r) => <span className="num">{r.active ? r.example : 'typed by hand'}</span> },
    { key: 'format', label: 'Number format', width: 220, optional: true, render: (r) => <span className="num">{r.format || `${r.prefix}{running_number}`}</span> },
    { key: 'separately', label: 'Separate numbering for', width: 200, optional: true, render: (r) => scopeWords(r.scope) },
    { key: 'resetPeriod', label: 'Reset period', width: 160, optional: true, render: (r) => RESETS[r.resetPeriod] ?? r.resetPeriod },
    { key: 'width', label: 'Min digits', width: 110, optional: true, render: (r) => <span className="num">{r.width}</span> },
    { key: 'nextValue', label: 'Next number', width: 120, optional: true, render: (r) => <span className="num">{r.nextValue}</span> },
  ]
  const docOptions = [...new Map((rules.data ?? []).map((r) => [r.docType, r.docTypeLabel])).entries()]
    .map(([value, label]) => ({ value, label }))

  return (
    <AdminList
      title="All Document Number Rules"
      scope="admin:numbering"
      rows={rules.data ?? []}
      columns={columns}
      getKey={(r) => r.id}
      onRowClick={(r) => setEditing(r)}
      // SET1-041 — a document type with no series yet can be given one here.
      actions={<Button kind="primary" icon={<Icon name="plus" size={18} />} onClick={() => setEditing(blankRule())}>Create Numbering Rule</Button>}
      filters={[
        { key: 'docType', label: 'Document', options: docOptions, matches: (r, v) => r.docType === v },
        { key: 'active', label: 'Active', options: YES_NO, matches: (r, v) => r.active === (v === 'yes') },
      ]}
      exportColumns={[
        { header: 'Document', value: (r) => r.docTypeLabel },
        { header: 'Active', value: (r) => (r.active ? 'Yes' : 'No') },
        { header: 'Number format', value: (r) => r.format || `${r.prefix}{running_number}` },
        { header: 'Separate numbering for', value: (r) => scopeWords(r.scope) },
        { header: 'Reset period', value: (r) => RESETS[r.resetPeriod] ?? r.resetPeriod },
        { header: 'Next number', value: (r) => r.nextValue },
      ]}
    />
  )
}

/**
 * SET1-042 — a numbering rule as a record page: `Numbering Rule > Credit
 * Notes` with its Active chip, Close and Action ▾, then General Info, Number
 * Format and Starting Numbers, and the Team Board.
 */
const NumberRulePage = ({ initial, taken, onBack, onSaved }: {
  initial: NumberRule
  taken: readonly string[]
  onBack: () => void
  onSaved: (next: NumberRule[]) => void
}) => {
  const api = useApi()
  const admin = adminApi(api)
  const [rule, setRule] = useState<NumberRule>(initial)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  const [starting, setStarting] = useState<string | null>(null)
  const isNew = rule.id === ''
  const tokens = useQuery({
    queryKey: ['numberTokens', rule.docType],
    queryFn: () => admin.tokens(rule.docType),
    enabled: rule.docType !== '',
    staleTime: 600_000,
  })
  const save = useMutation({
    mutationFn: (r: NumberRule) => admin.saveNumbering({ ...r, ...(r.id ? {} : { id: undefined as never }) }),
    onSuccess: (next, r) => {
      onSaved(next)
      const same = next.find((x) => x.docType === r.docType)
      if (same) setRule(same)
      setError(null)
      setSaved(true)
    },
    onError: (err) => { setError(messageFor(err)); setSaved(false) },
  })
  const set = (patch: Partial<NumberRule>): void => { setRule({ ...rule, ...patch }); setSaved(false) }
  // Every document type that numbers itself and has no rule yet.
  const free = DOCUMENTS.filter((d) => !taken.includes(d.key)).map((d) => ({ value: d.key, label: d.labelPlural }))
  const picked = rule.scope.split(',').filter((p) => p && p !== 'COMPANY')
  const title = rule.docType ? docName(rule.docType, rule.docTypeLabel) : 'New'

  return (
    <>
      <button type="button" className="crumb link-button" onClick={onBack}><Icon name="left" size={15} /> All Document Number Rules</button>
      <div className="doc-head" style={{ alignItems: 'center' }}>
        <h2 className="page-title" style={{ margin: 0 }}>
          {`Numbering Rule > ${title}`} {!isNew ? <span className="chip">{rule.active ? 'Active' : 'Inactive'}</span> : null}
        </h2>
        <div className="doc-actions">
          <Button kind="ghost" onClick={onBack}>Close</Button>
          <MenuButton label="Action" kind="primary" icon="down" showChevron disabled={save.isPending}
            items={[
              { key: 'save', label: 'Save', onSelect: () => {
                if (!rule.docType) { setError('Choose the document this rule numbers.'); return }
                save.mutate(rule)
              } },
              ...(isNew ? [] : [{
                key: 'active', label: rule.active ? 'Deactivate' : 'Activate', danger: rule.active,
                onSelect: () => save.mutate({ ...rule, active: !rule.active }),
              }]),
            ]} />
        </div>
      </div>
      {error ? <div style={{ margin: '12px 0' }}><Notice kind="bad">{error}</Notice></div> : null}
      {saved ? <div style={{ margin: '12px 0' }}><Notice kind="ok">Saved.</Notice></div> : null}
      {!rule.active ? <Notice kind="info">This rule is inactive: documents of this type are numbered by hand when they are submitted.</Notice> : null}

      <div className="record-split">
        <div>
          <Panel title="General Info">
            <div className="form-grid">
              <Field label="Document" required>
                {isNew ? (
                  <Select value={rule.docType} placeholder="Choose a document" options={free}
                    onChange={(docType) => set({ docType, docTypeLabel: free.find((f) => f.value === docType)?.label ?? docType })} />
                ) : <Input value={title} disabled onChange={() => {}} />}
              </Field>
              <Field label="Reset Period" required>
                <Select value={rule.resetPeriod} onChange={(resetPeriod) => set({ resetPeriod })}
                  options={Object.entries(RESETS).map(([value, label]) => ({ value, label }))} />
              </Field>
              <Field label="Min Digits" required hint="4 pads 28 to 0028.">
                <Select value={String(rule.width)} onChange={(w) => set({ width: Number(w) })}
                  options={[1, 2, 3, 4, 5, 6, 7, 8].map((n) => ({ value: String(n), label: String(n) }))} />
              </Field>
              <Field label="Separate Numbering for" hint="Left empty, there is one counter for the company.">
                <ChipChoice label="Separate Numbering for" options={SEPARATE_PICKS} value={picked}
                  onChange={(parts) => set({ scope: ['COMPANY', ...parts].join(',') })} />
              </Field>
            </div>
          </Panel>

          <Panel title="Number Format">
            <Field label="Format" required hint="Click a tag to add it; type the text between tags.">
              <FormatInput value={rule.format} onChange={(format) => set({ format })} />
            </Field>
            <div className="tokens">
              {(tokens.data ?? []).filter((t) => t.token !== '{prefix}').map((t) => (
                <button key={t.token} type="button" className="token" title={t.means}
                  onClick={() => set({ format: `${rule.format}${t.token}` })}>{t.token.slice(1, -1)}</button>
              ))}
            </div>
            <p className="helper">
              company: Company Abbre | Job Type: Job Type Abbre | Tax Unit: Tax Unit Abbre | MM: month in number | MMM: month in letters | fy: Financial year | cy: Calendar year
            </p>
            {rule.format.includes('{job_type}') ? (
              <Field label="Job Type Placeholder" hint="If the number format includes the job type tag and the document does not have a job type, this text appears instead.">
                <Input value={rule.jobTypePlaceholder} onChange={(jobTypePlaceholder) => set({ jobTypePlaceholder })} />
              </Field>
            ) : null}
          </Panel>

          <Panel title="Starting Numbers">
            <Notice kind="info">If starting number is not set then &lsquo;1&rsquo; will be consider as the starting number by default</Notice>
            <p style={{ marginTop: 12 }}>
              <button type="button" className="linkish" onClick={() => setStarting(String(rule.nextValue))}>
                Check current number and set the starting number
              </button>
            </p>
          </Panel>
        </div>
        {!isNew ? <TeamBoard record={{ type: 'number_series', id: rule.id }} /> : null}
      </div>

      {starting !== null ? (
        <Modal title="Starting number" onClose={() => setStarting(null)}
          actions={<><Button kind="ghost" onClick={() => setStarting(null)}>Cancel</Button>
            <Button kind="primary" disabled={!(Number(starting) >= (isNew ? 1 : initial.nextValue))}
              onClick={() => { set({ nextValue: Number(starting) }); setStarting(null) }}>Done</Button></>}>
          <p>{isNew ? 'Nothing has been numbered by this rule yet.' : `The next number this rule will give is ${initial.nextValue}.`}</p>
          <Field label="Starting number" hint="It can be moved forward, never back. Save the rule to keep it.">
            <Input value={starting} onChange={(v) => setStarting(v.replace(/\D/g, ''))} />
          </Field>
        </Modal>
      ) : null}
    </>
  )
}
